import { TimingPacket, timingSurfaces } from '../types/learningTiming';
import { useAuthStore } from '../store/authStore';
interface Entry { packet: TimingPacket; created: number }
function metadataOnly(packet: TimingPacket): boolean {
  if (!packet || typeof packet !== 'object' || !packet.scope || typeof packet.scope !== 'object') return false;
  if (Object.keys(packet).some(k => !['timingVersion', 'visitId', 'timelineId', 'sequence', 'kind', 'scope',
    'contentRevision', 'startOffsetMs', 'endOffsetMs', 'elapsedMs', 'reason', 'lossDetected', 'coordination'].includes(k))) return false;
  if (Object.keys(packet.scope).some(k => !['context', 'surface', 'courseId', 'chapterId', 'sectionId',
    'topicId', 'activityId', 'contentItemId', 'platformKey'].includes(k))) return false;
  const id = /^[a-f0-9]{24}$/, uuid = /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/;
  return packet.timingVersion === 'foreground_elapsed_v1' && uuid.test(packet.visitId) && uuid.test(packet.timelineId) &&
    ['course', 'topic', 'platform'].includes(packet.scope.context) && timingSurfaces.includes(packet.scope.surface) &&
    ['courseId', 'chapterId', 'sectionId', 'topicId', 'activityId'].every(k => {
      const v = packet.scope[k as keyof typeof packet.scope]; return v === undefined || id.test(v);
    }) && (packet.scope.contentItemId === undefined || /^[a-zA-Z0-9_-]{1,80}$/.test(packet.scope.contentItemId)) &&
    (packet.scope.platformKey === undefined || packet.scope.platformKey === 'workspace') &&
    (packet.contentRevision === null || id.test(packet.contentRevision)) &&
    ['open', 'interval', 'close'].includes(packet.kind) &&
    ['checkpoint', 'navigation', 'hidden', 'blur', 'pause', 'suspension', 'pagehide', 'logout',
      'queue_loss', 'unsupported', 'rotation'].includes(packet.reason) &&
    [packet.sequence, packet.startOffsetMs, packet.endOffsetMs, packet.elapsedMs].every(n =>
      typeof n === 'number' && Number.isSafeInteger(n) && n >= 0 && n <= 86400000) &&
    typeof packet.lossDetected === 'boolean' && packet.coordination === 'unverified' &&
    JSON.stringify(packet).length <= 2048;
}
/** One tab's queue, partitioned by actor. No credentials persisted, IDs never regenerated. */
export class TimingQueue {
  private entries: Entry[] = [];
  private loss = false;
  private busy = false;
  private failures = 0;
  private nextTry = 0;
  constructor(private actor: string, private storage: Pick<Storage, 'getItem' | 'setItem'>,
    private now = Date.now) {
    try {
      this.loss = storage.getItem(`${this.key()}:loss`) === '1';
      const raw = storage.getItem(this.key());
      if (raw && raw.length <= 262144) {
        const parsed: unknown = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length <= 256) this.entries = parsed;
        else this.loss = true;
      } else if (raw) this.loss = true;
    } catch { this.loss = true; }
    this.trim();
  }
  private key() { return `foreground-v1:${this.actor}`; }
  private trim() {
    const before = this.entries.length;
    this.entries = this.entries.filter(e => e && Object.keys(e).every(k => ['packet', 'created'].includes(k)) && metadataOnly(e.packet) &&
      Number.isFinite(e.created) && this.now() - e.created <= 86400000 && this.now() >= e.created);
    while (this.entries.length > 256 || JSON.stringify(this.entries).length > 250000) this.entries.shift();
    if (this.entries.length !== before) this.loss = true;
  }
  private save() {
    try {
      this.storage.setItem(this.key(), JSON.stringify(this.entries));
      this.storage.setItem(`${this.key()}:loss`, this.loss ? '1' : '0');
    }
    catch { this.loss = true; console.warn('[learning-timing] queue storage unavailable; capture partial'); }
  }
  enqueue(packet: TimingPacket) {
    this.trim();
    if (!metadataOnly(packet)) {
      this.loss = true; this.save(); console.warn('[learning-timing] invalid local metadata; capture partial'); return;
    }
    while (this.entries.length >= 256 || JSON.stringify(this.entries).length > 247000) {
      this.entries.shift(); this.loss = true;
    }
    // Only annotate a never-sent packet. A retry must remain byte-identical.
    const next = {...packet, scope: {...packet.scope}, lossDetected: packet.lossDetected || this.loss};
    this.loss = false;
    this.entries.push({packet: next, created: this.now()});
    this.save();
  }
  async drain(send: (packet: TimingPacket) => Promise<number>, isActor: () => boolean, bestEffortUnload = false) {
    if (this.busy || !isActor() || (!bestEffortUnload && this.now() < this.nextTry)) return;
    this.busy = true;
    try {
      this.trim();
      // Avoid bursts exceeding the endpoint's bounded per-actor rate.
      for (let n = 0; n < 4 && this.entries.length && isActor(); n++) {
        const entry = this.entries[0];
        let status = 0;
        try { status = await send(entry.packet); } catch { /* immutable retry below */ }
        if (status >= 200 && status < 300) {
          const index = this.entries.indexOf(entry);
          if (index >= 0) this.entries.splice(index, 1);
          this.failures = 0; this.save();
        } else if ([400, 403, 409, 413].includes(status)) {
          const index = this.entries.indexOf(entry);
          if (index >= 0) this.entries.splice(index, 1);
          this.loss = true; this.save();
          console.warn('[learning-timing] segment rejected; capture partial', status);
        } else {
          this.nextTry = this.now() + Math.min(60000, 1000 * 2 ** Math.min(++this.failures, 6)) + Math.floor(Math.random() * 500);
          break;
        }
      }
    } finally { this.nextTry = Math.max(this.nextTry, this.now() + 4000); this.busy = false; }
  }
}
const queues = new Map<string, TimingQueue>();
export function timingQueue(actor: string) {
  if (!queues.has(actor)) {
    let storage: Pick<Storage, 'getItem' | 'setItem'>;
    try { storage = sessionStorage; }
    catch { storage = {getItem: () => null, setItem: () => { throw new Error('unavailable'); }}; }
    queues.set(actor, new TimingQueue(actor, storage));
  }
  return queues.get(actor)!;
}
export function deliverTiming(actor: string, keepalive = false) {
  const identity = () => useAuthStore.getState().user?.id === actor && !!useAuthStore.getState().token;
  return timingQueue(actor).drain(async packet => {
    if (!identity()) return 401;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/learning-timing`, {
        method: 'POST', headers: {'Content-Type': 'application/json',
          Authorization: `Bearer ${useAuthStore.getState().token}`},
        body: JSON.stringify(packet), keepalive, signal: controller.signal
      });
      return response.status;
    } finally { window.clearTimeout(timeout); }
  }, identity, keepalive);
}
